import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Settings, Save, RotateCcw, Plus, Trash2, Edit2, Check, X,
  Palette, DoorOpen, Grid3X3, Wrench, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const ConfiguratorSettingsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');
  const [settings, setSettings] = useState({
    colors: [],
    doors: [],
    windows: [],
    services: [],
    styles: []
  });
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/configurator-settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load configurator settings');
    } finally {
      setLoading(false);
    }
  };

  const saveAllSettings = async () => {
    try {
      setSaving(true);
      await api.put('/configurator-settings', settings);
      toast.success('Settings saved and synced to pricing');
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm('Reset all configurator settings to defaults? This cannot be undone.')) return;
    try {
      setSaving(true);
      await api.post('/configurator-settings/reset');
      toast.success('Settings reset to defaults');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  // Generic handlers
  const handleItemChange = (category, index, field, value) => {
    const updated = { ...settings };
    updated[category][index] = { ...updated[category][index], [field]: value };
    setSettings(updated);
  };

  const handleAddItem = (category, item) => {
    const updated = { ...settings };
    updated[category] = [...updated[category], item];
    setSettings(updated);
    setNewItem(null);
  };

  const handleDeleteItem = (category, index) => {
    if (!window.confirm('Delete this item?')) return;
    const updated = { ...settings };
    updated[category] = updated[category].filter((_, i) => i !== index);
    setSettings(updated);
  };

  const tabs = [
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'doors', label: 'Doors', icon: DoorOpen },
    { id: 'windows', label: 'Windows', icon: Grid3X3 },
    { id: 'services', label: 'Services', icon: Wrench },
    { id: 'styles', label: 'Building Styles', icon: Building2 },
  ];

  const colorCategories = ['roof', 'trim', 'sides', 'wainscot'];
  const doorTypes = ['garage', 'rollup', 'walk'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C41E3A]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Settings className="w-7 h-7 text-[#C41E3A]" />
              Configurator Settings
            </h1>
            <p className="text-gray-500 mt-1">
              Manage colors, doors, windows, and other configurator options
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetSettings} disabled={saving}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button onClick={saveAllSettings} disabled={saving} className="bg-[#C41E3A] hover:bg-[#a01830] text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#C41E3A] text-[#C41E3A]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="space-y-6">
            {colorCategories.map((category) => {
              const categoryColors = settings.colors.filter(c => c.category === category);
              return (
                <div key={category} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800 capitalize">{category} Colors</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setNewItem({ category, type: 'color', id: '', name: '', value: '#000000', active: true })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Color
                    </Button>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {categoryColors.map((color, idx) => {
                        const globalIdx = settings.colors.findIndex(c => c.id === color.id);
                        return (
                          <div
                            key={color.id}
                            className={`relative p-3 border rounded-lg ${color.active ? 'bg-white' : 'bg-gray-100 opacity-60'}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div
                                className="w-8 h-8 rounded-full border-2 border-gray-200"
                                style={{ backgroundColor: color.value !== 'none' ? color.value : '#f3f4f6' }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{color.name}</p>
                                <p className="text-xs text-gray-400">{color.value}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <input
                                type="color"
                                value={color.value !== 'none' ? color.value : '#ffffff'}
                                onChange={(e) => handleItemChange('colors', globalIdx, 'value', e.target.value)}
                                className="w-8 h-6 cursor-pointer"
                                disabled={color.value === 'none'}
                              />
                              <Input
                                value={color.value !== 'none' ? color.value : ''}
                                onChange={(e) => handleItemChange('colors', globalIdx, 'value', e.target.value)}
                                placeholder="#HEX"
                                className="text-xs h-6 w-20 font-mono"
                                disabled={color.value === 'none'}
                              />
                              <Input
                                value={color.name}
                                onChange={(e) => handleItemChange('colors', globalIdx, 'name', e.target.value)}
                                className="text-xs h-6 flex-1"
                              />
                              <button
                                onClick={() => handleDeleteItem('colors', globalIdx)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            <label className="flex items-center gap-1 mt-2 text-xs">
                              <input
                                type="checkbox"
                                checked={color.active}
                                onChange={(e) => handleItemChange('colors', globalIdx, 'active', e.target.checked)}
                              />
                              Active
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* New Color Modal */}
            {newItem?.type === 'color' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Add New Color</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <select
                        value={newItem.category}
                        onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                      >
                        {colorCategories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">ID (unique)</label>
                      <Input
                        value={newItem.id}
                        onChange={(e) => setNewItem({ ...newItem, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="e.g., roof_navy"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="e.g., Navy Blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={newItem.value}
                          onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                          className="w-12 h-10 cursor-pointer"
                        />
                        <Input
                          value={newItem.value}
                          onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setNewItem(null)}>Cancel</Button>
                      <Button
                        onClick={() => {
                          if (!newItem.id || !newItem.name) {
                            toast.error('Please fill in all fields');
                            return;
                          }
                          handleAddItem('colors', newItem);
                          toast.success('Color added');
                        }}
                        className="bg-[#C41E3A] hover:bg-[#a01830] text-white"
                      >
                        Add Color
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Doors Tab */}
        {activeTab === 'doors' && (
          <div className="space-y-6">
            {doorTypes.map((doorType) => {
              const typeDoors = settings.doors.filter(d => d.door_type === doorType);
              return (
                <div key={doorType} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800 capitalize">{doorType} Doors</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setNewItem({ type: 'door', door_type: doorType, id: '', name: '', width: 8, height: 7, price: 500, description: '', active: true })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add {doorType} Door
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50/50">
                        <tr>
                          <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Name</th>
                          <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Size (W×H)</th>
                          <th className="text-right py-2 px-4 font-medium text-gray-600 text-sm">Price ($)</th>
                          <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Description</th>
                          <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Active</th>
                          <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {typeDoors.map((door) => {
                          const globalIdx = settings.doors.findIndex(d => d.id === door.id);
                          return (
                            <tr key={door.id} className="border-t hover:bg-gray-50">
                              <td className="py-2 px-4">
                                <Input
                                  value={door.name}
                                  onChange={(e) => handleItemChange('doors', globalIdx, 'name', e.target.value)}
                                  className="text-sm"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <div className="flex items-center justify-center gap-1">
                                  <Input
                                    type="number"
                                    value={door.width}
                                    onChange={(e) => handleItemChange('doors', globalIdx, 'width', parseInt(e.target.value) || 0)}
                                    className="text-sm w-16 text-center"
                                  />
                                  <span>×</span>
                                  <Input
                                    type="number"
                                    value={door.height}
                                    onChange={(e) => handleItemChange('doors', globalIdx, 'height', parseInt(e.target.value) || 0)}
                                    className="text-sm w-16 text-center"
                                  />
                                </div>
                              </td>
                              <td className="py-2 px-4">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={door.price}
                                  onChange={(e) => handleItemChange('doors', globalIdx, 'price', parseFloat(e.target.value) || 0)}
                                  className="text-sm text-right w-24 ml-auto"
                                />
                              </td>
                              <td className="py-2 px-4">
                                <Input
                                  value={door.description || ''}
                                  onChange={(e) => handleItemChange('doors', globalIdx, 'description', e.target.value)}
                                  className="text-sm"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="py-2 px-4 text-center">
                                <input
                                  type="checkbox"
                                  checked={door.active}
                                  onChange={(e) => handleItemChange('doors', globalIdx, 'active', e.target.checked)}
                                />
                              </td>
                              <td className="py-2 px-4 text-center">
                                <button
                                  onClick={() => handleDeleteItem('doors', globalIdx)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {/* New Door Modal */}
            {newItem?.type === 'door' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Add New {newItem.door_type} Door</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">ID (unique)</label>
                      <Input
                        value={newItem.id}
                        onChange={(e) => setNewItem({ ...newItem, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="e.g., garage_18x14"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="e.g., Garage Door 18'x14'"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Width (ft)</label>
                        <Input
                          type="number"
                          value={newItem.width}
                          onChange={(e) => setNewItem({ ...newItem, width: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Height (ft)</label>
                        <Input
                          type="number"
                          value={newItem.height}
                          onChange={(e) => setNewItem({ ...newItem, height: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price ($)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <Input
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setNewItem(null)}>Cancel</Button>
                      <Button
                        onClick={() => {
                          if (!newItem.id || !newItem.name) {
                            toast.error('Please fill in required fields');
                            return;
                          }
                          handleAddItem('doors', newItem);
                          toast.success('Door added - remember to save!');
                        }}
                        className="bg-[#C41E3A] hover:bg-[#a01830] text-white"
                      >
                        Add Door
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Windows Tab */}
        {activeTab === 'windows' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 dark:text-white">Window Options</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewItem({ type: 'window', id: '', name: '', width: 3, height: 3, price: 275, description: '', active: true })}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Window
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Name</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Size (W×H)</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600 text-sm">Price ($)</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Description</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Active</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.windows.map((window, index) => (
                    <tr key={window.id} className="border-t hover:bg-gray-50">
                      <td className="py-2 px-4">
                        <Input
                          value={window.name}
                          onChange={(e) => handleItemChange('windows', index, 'name', e.target.value)}
                          className="text-sm"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number"
                            value={window.width}
                            onChange={(e) => handleItemChange('windows', index, 'width', parseInt(e.target.value) || 0)}
                            className="text-sm w-16 text-center"
                          />
                          <span>×</span>
                          <Input
                            type="number"
                            value={window.height}
                            onChange={(e) => handleItemChange('windows', index, 'height', parseInt(e.target.value) || 0)}
                            className="text-sm w-16 text-center"
                          />
                        </div>
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          value={window.price}
                          onChange={(e) => handleItemChange('windows', index, 'price', parseFloat(e.target.value) || 0)}
                          className="text-sm text-right w-24 ml-auto"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          value={window.description || ''}
                          onChange={(e) => handleItemChange('windows', index, 'description', e.target.value)}
                          className="text-sm"
                          placeholder="Description"
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={window.active}
                          onChange={(e) => handleItemChange('windows', index, 'active', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <button
                          onClick={() => handleDeleteItem('windows', index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* New Window Modal */}
            {newItem?.type === 'window' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Add New Window</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">ID (unique)</label>
                      <Input
                        value={newItem.id}
                        onChange={(e) => setNewItem({ ...newItem, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="e.g., window_6x6"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="e.g., Window 6'x6'"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Width (ft)</label>
                        <Input
                          type="number"
                          value={newItem.width}
                          onChange={(e) => setNewItem({ ...newItem, width: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Height (ft)</label>
                        <Input
                          type="number"
                          value={newItem.height}
                          onChange={(e) => setNewItem({ ...newItem, height: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price ($)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setNewItem(null)}>Cancel</Button>
                      <Button
                        onClick={() => {
                          if (!newItem.id || !newItem.name) {
                            toast.error('Please fill in required fields');
                            return;
                          }
                          handleAddItem('windows', newItem);
                          toast.success('Window added - remember to save!');
                        }}
                        className="bg-[#C41E3A] hover:bg-[#a01830] text-white"
                      >
                        Add Window
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 dark:text-white">Service Options</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewItem({ type: 'service', id: '', name: '', price: 100, description: '', fees_apply: false, active: true })}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Service
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Name</th>
                    <th className="text-right py-2 px-4 font-medium text-gray-600 text-sm">Price ($)</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Description</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Fees Apply</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Active</th>
                    <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.services.map((service, index) => (
                    <tr key={service.id} className="border-t hover:bg-gray-50">
                      <td className="py-2 px-4">
                        <Input
                          value={service.name}
                          onChange={(e) => handleItemChange('services', index, 'name', e.target.value)}
                          className="text-sm"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          value={service.price}
                          onChange={(e) => handleItemChange('services', index, 'price', parseFloat(e.target.value) || 0)}
                          className="text-sm text-right w-24 ml-auto"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          value={service.description || ''}
                          onChange={(e) => handleItemChange('services', index, 'description', e.target.value)}
                          className="text-sm"
                          placeholder="Description"
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={service.fees_apply}
                          onChange={(e) => handleItemChange('services', index, 'fees_apply', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={service.active}
                          onChange={(e) => handleItemChange('services', index, 'active', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <button
                          onClick={() => handleDeleteItem('services', index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* New Service Modal */}
            {newItem?.type === 'service' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Add New Service</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">ID (unique)</label>
                      <Input
                        value={newItem.id}
                        onChange={(e) => setNewItem({ ...newItem, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="e.g., custom_foundation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="e.g., Custom Foundation Work"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price ($)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <Input
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      />
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newItem.fees_apply}
                        onChange={(e) => setNewItem({ ...newItem, fees_apply: e.target.checked })}
                      />
                      <span className="text-sm">Fees Apply (show warning)</span>
                    </label>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setNewItem(null)}>Cancel</Button>
                      <Button
                        onClick={() => {
                          if (!newItem.id || !newItem.name) {
                            toast.error('Please fill in required fields');
                            return;
                          }
                          handleAddItem('services', newItem);
                          toast.success('Service added - remember to save!');
                        }}
                        className="bg-[#C41E3A] hover:bg-[#a01830] text-white"
                      >
                        Add Service
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Building Styles Tab */}
        {activeTab === 'styles' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-800 dark:text-white">Building Styles</h3>
              <p className="text-sm text-gray-500">Configure available building style options and their default settings</p>
            </div>
            <div className="p-4 space-y-4">
              {settings.styles.map((style, index) => (
                <div key={style.id} className={`border rounded-lg p-4 ${style.active ? '' : 'opacity-60 bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={style.active}
                        onChange={(e) => handleItemChange('styles', index, 'active', e.target.checked)}
                      />
                      <Input
                        value={style.name}
                        onChange={(e) => handleItemChange('styles', index, 'name', e.target.value)}
                        className="font-semibold text-lg w-48"
                      />
                    </div>
                    <button
                      onClick={() => handleDeleteItem('styles', index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    value={style.description}
                    onChange={(e) => handleItemChange('styles', index, 'description', e.target.value)}
                    className="text-sm mb-3"
                    placeholder="Description"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <label className="text-gray-500">Width</label>
                      <Input
                        type="number"
                        value={style.defaults?.width || 20}
                        onChange={(e) => handleItemChange('styles', index, 'defaults', { ...style.defaults, width: parseInt(e.target.value) || 20 })}
                      />
                    </div>
                    <div>
                      <label className="text-gray-500">Length</label>
                      <Input
                        type="number"
                        value={style.defaults?.length || 21}
                        onChange={(e) => handleItemChange('styles', index, 'defaults', { ...style.defaults, length: parseInt(e.target.value) || 21 })}
                      />
                    </div>
                    <div>
                      <label className="text-gray-500">Leg Height</label>
                      <Input
                        type="number"
                        value={style.defaults?.legHeight || 8}
                        onChange={(e) => handleItemChange('styles', index, 'defaults', { ...style.defaults, legHeight: parseInt(e.target.value) || 8 })}
                      />
                    </div>
                    <div>
                      <label className="text-gray-500">Roof Pitch</label>
                      <Input
                        type="number"
                        value={style.defaults?.roofPitch || 3}
                        onChange={(e) => handleItemChange('styles', index, 'defaults', { ...style.defaults, roofPitch: parseInt(e.target.value) || 3 })}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Changes made here will be synced to the Pricing page when you save. 
            Door, window, and service prices will automatically update in the pricing features list.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConfiguratorSettingsPage;
